import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../AuthContext';
import { API_BASE, API_KEY } from '../config';

type UserRole = 'admin' | 'user-full' | 'user-reader';

interface AdminUser {
  id: string;
  email: string;
  fullName: string;
  role: UserRole;
  approved?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

async function adminFetch<T>(endpoint: string, token: string | null, options: RequestInit = {}): Promise<T> {
  const separator = endpoint.includes('?') ? '&' : '?';
  const urlWithKey = API_KEY ? `${API_BASE}${endpoint}${separator}code=${API_KEY}` : `${API_BASE}${endpoint}`;
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(urlWithKey, { ...options, headers });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
  return data;
}

function roleLabel(role: UserRole): string {
  if (role === 'user-reader') return 'reader';
  if (role === 'user-full') return 'full';
  return role;
}

function roleFromLabel(value: string): UserRole {
  if (value === 'reader') return 'user-reader';
  if (value === 'full') return 'user-full';
  return 'admin';
}

export default function AdminUsers() {
  const { token, user } = useAuth();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyUserId, setBusyUserId] = useState<string | null>(null);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await adminFetch<{ users: AdminUser[] }>('/auth/admin/users', token);
      setUsers(data.users || []);
    } catch (e: any) {
      setError(e.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const handleApprove = async (target: AdminUser, approved: boolean) => {
    setBusyUserId(target.id);
    setError('');
    try {
      await adminFetch('/auth/admin/users/approve', token, {
        method: 'POST',
        body: JSON.stringify({ userId: target.id, approved }),
      });
      await loadUsers();
    } catch (e: any) {
      setError(e.message || 'Failed to update approval');
    } finally {
      setBusyUserId(null);
    }
  };

  const handleRoleChange = async (target: AdminUser, role: UserRole) => {
    setBusyUserId(target.id);
    setError('');
    try {
      await adminFetch('/auth/admin/users/role', token, {
        method: 'POST',
        body: JSON.stringify({ userId: target.id, role }),
      });
      await loadUsers();
    } catch (e: any) {
      setError(e.message || 'Failed to change role');
    } finally {
      setBusyUserId(null);
    }
  };

  const handleDelete = async (target: AdminUser) => {
    if (!window.confirm(`Delete user ${target.email}? This cannot be undone.`)) return;
    setBusyUserId(target.id);
    setError('');
    try {
      await adminFetch('/auth/admin/users/delete', token, {
        method: 'POST',
        body: JSON.stringify({ userId: target.id }),
      });
      await loadUsers();
    } catch (e: any) {
      setError(e.message || 'Failed to delete user');
    } finally {
      setBusyUserId(null);
    }
  };

  if (loading) return <div className="loading">Loading users…</div>;

  return (
    <div>
      <h2>Admin Users</h2>
      <p style={{ color: 'var(--text-muted)', marginBottom: 16 }}>
        Approve access, change user roles, and delete accounts.
      </p>
      {error && <div className="error-msg">{error}</div>}

      <div className="portfolio-table-wrap">
        <table className="portfolio-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Approved</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => {
              const isSelf = u.id === user?.id;
              const busy = busyUserId === u.id;
              return (
                <tr key={u.id}>
                  <td>{u.fullName}</td>
                  <td>{u.email}</td>
                  <td>
                    <select
                      value={roleLabel(u.role)}
                      disabled={busy || isSelf}
                      onChange={(e) => handleRoleChange(u, roleFromLabel(e.target.value))}
                      style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text)' }}
                    >
                      <option value="reader">reader</option>
                      <option value="full">full</option>
                      <option value="admin">admin</option>
                    </select>
                  </td>
                  <td>
                    {u.approved ? (
                      <span style={{ color: 'var(--green)' }}>Approved</span>
                    ) : (
                      <span style={{ color: 'var(--red)' }}>Pending</span>
                    )}
                  </td>
                  <td>{u.createdAt ? new Date(u.createdAt).toLocaleDateString() : '—'}</td>
                  <td className="action-btns">
                    <button
                      className="sell-btn"
                      disabled={busy}
                      onClick={() => handleApprove(u, !u.approved)}
                    >
                      {u.approved ? 'Revoke' : 'Approve'}
                    </button>
                    <button
                      className="remove-btn"
                      disabled={busy || isSelf}
                      onClick={() => handleDelete(u)}
                      title={isSelf ? 'Cannot delete yourself' : 'Delete user'}
                    >
                      ✕
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
